import {
  initialize,
  type LDClient,
  type LDContext,
} from 'launchdarkly-react-client-sdk';

type LdUser = {
  userId: string;
  currentRole: string;
  currentOrg: { orgId: string };
};

let client: LDClient | undefined;
let identified: { signature: string; done: Promise<void> } | undefined;

/**
 * The one LaunchDarkly client. `asyncWithLDProvider` is given this instance, so
 * route loaders and components read the same flags from the same evaluation.
 */
export function createLdClient(clientSideID: string) {
  client = initialize(clientSideID, { kind: 'user', key: 'staticKey' });
  return client;
}

export function toLdContext(user: LdUser): LDContext {
  return {
    kind: 'user',
    key: user.userId,
    orgId: user.currentOrg.orgId,
    role: user.currentRole,
  };
}

/** Concurrent callers share one request; later calls for the same user reuse it. */
export function identifyUser(user: LdUser): Promise<void> {
  if (!client) return Promise.resolve();

  const context = toLdContext(user);
  const signature = JSON.stringify(context);
  if (identified?.signature !== signature) {
    identified = {
      signature,
      done: client.identify(context).then(
        () => undefined,
        (err) => console.error('Could not identify user to LaunchDarkly', err)
      ),
    };
  }
  return identified.done;
}

/**
 * Reads a flag outside React. The client bootstraps anonymously, so it is
 * identified for this user first — targeting rules keyed on the user would
 * otherwise be evaluated for nobody.
 */
export async function getFlag<T>(
  user: LdUser,
  key: string,
  fallback: T
): Promise<T> {
  if (!client) return fallback;
  await identifyUser(user);
  return client.variation(key, fallback) as T;
}
